import React, { useEffect, useRef } from 'react';
// import { Mui } from '@osu/react-ui';
import { useAdminContext } from '../context/AdminContext';

/**
 * ReadOnlyEnforcer Higher-Order Component
 * 
 * This HOC wraps components to enforce read-only behavior in admin view mode.
 * It intercepts form submissions, disables interactive elements, and provides
 * visual feedback for disabled states while maintaining accessibility.
 * 
 * Features:
 * - Intercepts and prevents form submissions
 * - Disables buttons, inputs, and other interactive elements
 * - Adds visual indicators for disabled state
 * - Maintains accessibility attributes
 * - Provides tooltips explaining why elements are disabled
 * 
 * @param {React.Component} WrappedComponent - Component to wrap with read-only enforcement
 * @returns {React.Component} Enhanced component with read-only enforcement
 */
function withReadOnlyEnforcer(WrappedComponent) {
  const ReadOnlyEnforcedComponent = React.forwardRef((props, ref) => {
    const { isAdminView } = useAdminContext();
    const containerRef = useRef(null);
    const originalHandlers = useRef(new Map());

    /**
     * Intercepts and prevents form submission events
     */
    const handleFormSubmit = (event) => {
      event.preventDefault();
      event.stopPropagation();

      // Show user feedback (development only)
      if (process.env.NODE_ENV === 'development') {
        console.log('Form submission prevented in admin view mode');
      }

      // Could add toast notification here in the future
      return false;
    };

    /**
     * Determines if a button should be allowed (external links, info buttons, etc.)
     */
    const shouldAllowButton = (element) => {
      // Allow external links
      if (element.tagName === 'A' || element.closest('a')) {
        return true;
      }

      // Allow buttons with external URLs
      const href = element.getAttribute('href') || element.getAttribute('data-href');
      if (href && (href.startsWith('http') || href.startsWith('mailto:'))) {
        return true;
      }

      // Allow buttons marked as safe
      if (element.hasAttribute('data-admin-safe') || element.closest('[data-admin-safe]')) {
        return true;
      }

      // Allow informational buttons and form-opening buttons (common patterns)
      const buttonText = element.textContent?.toLowerCase() || '';
      const allowedPatterns = [
        'learn more', 'view details', 'more info', 'help', 'faq',
        'download', 'print', 'export', 'view', 'show', 'expand', 'collapse',
        'request preferences', 'accept invitation', 'explore', 'learn about',
        'view program', 'program details', 'more information'
      ];

      if (allowedPatterns.some(pattern => buttonText.includes(pattern))) {
        return true;
      }

      // Allow buttons with specific classes (like toggle buttons for UI)
      const allowedClasses = [
        'toggle', 'expand', 'collapse', 'show', 'hide', 'view',
        'open-form', 'modal-trigger', 'dialog-trigger'
      ];

      if (allowedClasses.some(cls => element.className.includes(cls))) {
        return true;
      }

      // Block only submission/action buttons
      const blockPatterns = [
        'submit', 'save', 'confirm', 'send', 'apply', 'cancel', 'decline'
      ];

      // If it's a submission button, block it
      if (blockPatterns.some(pattern => buttonText.includes(pattern))) {
        return false;
      }

      // Allow all other buttons (default to permissive)
      return true;
    };

    /**
     * Intercepts and prevents button click events (selective)
     */
    const handleButtonClick = (event) => {
      // Allow certain buttons to work normally
      if (shouldAllowButton(event.target)) {
        return true;
      }

      event.preventDefault();
      event.stopPropagation();

      // Show user feedback (development only)
      if (process.env.NODE_ENV === 'development') {
        console.log('Button click prevented in admin view mode');
      }

      return false;
    };

    /**
     * Intercepts and prevents input change events
     */
    const handleInputChange = (event) => {
      event.preventDefault();
      event.stopPropagation();

      return false;
    };

    /**
     * Applies read-only enforcement to DOM elements
     */
    const applyReadOnlyEnforcement = () => {
      if (!containerRef.current || !isAdminView) return;

      const container = containerRef.current;

      // Find all interactive elements
      const interactiveElements = container.querySelectorAll(
        'button, input, textarea, select, [role="button"], [tabindex="0"]'
      );

      interactiveElements.forEach((element) => {
        // Skip if already processed
        if (element.hasAttribute('data-readonly-enforced')) return;

        // Store original handlers
        const elementKey = element.getAttribute('data-element-id') || Math.random().toString();
        element.setAttribute('data-element-id', elementKey);

        // Store original event handlers
        const originalOnClick = element.onclick;
        const originalOnSubmit = element.onsubmit;
        const originalOnChange = element.onchange;

        originalHandlers.current.set(elementKey, {
          onclick: originalOnClick,
          onsubmit: originalOnSubmit,
          onchange: originalOnChange
        });

        // Apply read-only enforcement based on element type
        if (element.tagName === 'BUTTON' || element.getAttribute('role') === 'button') {
          // Check if this button should be allowed
          if (shouldAllowButton(element)) {
            // Mark as processed but don't disable
            element.setAttribute('data-readonly-enforced', 'true');
            return;
          }
          
          // Log what we're blocking for debugging (development only)
          if (process.env.NODE_ENV === 'development') {
            const buttonText = element.textContent?.toLowerCase() || '';
            console.log('Blocking button in admin view:', buttonText);
          }

          // Disable buttons that should be blocked
          element.disabled = true;
          element.setAttribute('aria-disabled', 'true');
          element.setAttribute('title', 'Actions are disabled in admin view mode');
          element.onclick = handleButtonClick;

          // Add visual styling for disabled state
          element.style.opacity = '0.6';
          element.style.cursor = 'not-allowed';
          element.style.pointerEvents = 'none';

        } else if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') {
          // Check if this is a form input that should be disabled
          const shouldDisableInput =
            // Only disable submit buttons specifically
            element.type === 'submit' ||
            // Disable inputs marked as admin-restricted
            element.hasAttribute('data-admin-restrict') ||
            element.closest('[data-admin-restrict]');

          if (shouldDisableInput) {
            // Disable form inputs that modify data
            element.disabled = true;
            element.setAttribute('aria-disabled', 'true');
            element.setAttribute('title', 'Form inputs are disabled in admin view mode');
            element.onchange = handleInputChange;
            element.oninput = handleInputChange;

            // Add visual styling for disabled state
            element.style.opacity = '0.7';
            element.style.cursor = 'not-allowed';
          }

        } else if (element.tagName === 'FORM') {
          // Intercept form submissions
          element.onsubmit = handleFormSubmit;
        }

        // Mark as processed
        element.setAttribute('data-readonly-enforced', 'true');
      });

      // Find and selectively disable Material-UI components
      const muiButtons = container.querySelectorAll('[class*="MuiButton"], [class*="MuiFab"], [class*="MuiIconButton"]');
      muiButtons.forEach((button) => {
        if (!button.hasAttribute('data-readonly-enforced')) {
          // Check if this button should be allowed
          if (shouldAllowButton(button)) {
            button.setAttribute('data-readonly-enforced', 'true');
            return;
          }

          // Disable buttons that should be blocked
          button.setAttribute('disabled', 'true');
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('title', 'Actions are disabled in admin view mode');
          button.style.opacity = '0.6';
          button.style.pointerEvents = 'none';
          button.setAttribute('data-readonly-enforced', 'true');
        }
      });

      // Find and selectively disable Material-UI form controls
      const muiInputs = container.querySelectorAll('[class*="MuiInput"], [class*="MuiTextField"], [class*="MuiSelect"]');
      muiInputs.forEach((input) => {
        if (!input.hasAttribute('data-readonly-enforced')) {
          // Check if this input should be disabled
          const shouldDisableInput =
            // Only disable inputs marked as admin-restricted
            input.hasAttribute('data-admin-restrict') ||
            input.closest('[data-admin-restrict]');

          if (shouldDisableInput) {
            const inputElement = input.querySelector('input, textarea, select');
            if (inputElement) {
              inputElement.disabled = true;
              inputElement.setAttribute('aria-disabled', 'true');
              inputElement.setAttribute('title', 'Form inputs are disabled in admin view mode');
            }
            input.style.opacity = '0.7';
          }
          input.setAttribute('data-readonly-enforced', 'true');
        }
      });
    };

    /**
     * Removes read-only enforcement from DOM elements
     */
    const removeReadOnlyEnforcement = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const enforcedElements = container.querySelectorAll('[data-readonly-enforced]');

      enforcedElements.forEach((element) => {
        const elementKey = element.getAttribute('data-element-id');
        const orgHandlers = originalHandlers.current.get(elementKey);

        if (orgHandlers) {
          // Restore original handlers
          element.onclick = orgHandlers.onclick;
          element.onsubmit = orgHandlers.onsubmit;
          element.onchange = orgHandlers.onchange;
        }

        // Remove disabled state
        element.removeAttribute('disabled');
        element.removeAttribute('aria-disabled');
        element.removeAttribute('title');
        element.style.opacity = '';
        element.style.cursor = '';
        element.style.pointerEvents = '';

        // Remove enforcement markers
        element.removeAttribute('data-readonly-enforced');
        element.removeAttribute('data-element-id');
      });

      // Clear stored handlers
      originalHandlers.current.clear();
    };

    // Apply/remove enforcement when admin view state changes
    useEffect(() => {
      if (isAdminView) {
        // Use setTimeout to ensure DOM is fully rendered
        const timeoutId = setTimeout(applyReadOnlyEnforcement, 100);

        // Set up mutation observer to handle dynamically added elements
        const observer = new MutationObserver((mutations) => {
          let shouldReapply = false;
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              shouldReapply = true;
            }
          });

          if (shouldReapply) {
            setTimeout(applyReadOnlyEnforcement, 50);
          }
        });

        if (containerRef.current) {
          observer.observe(containerRef.current, {
            childList: true,
            subtree: true
          });
        }

        return () => {
          clearTimeout(timeoutId);
          observer.disconnect();
        };
      } else {
        removeReadOnlyEnforcement();
      }
    }, [isAdminView]);

    // Clean up on unmount
    useEffect(() => {
      return () => {
        removeReadOnlyEnforcement();
      };
    }, []);

    // If not in admin view, render normally
    if (!isAdminView) {
      return <WrappedComponent {...props} ref={ref} />;
    }

    // Render with read-only enforcement
    return (
      <div
        ref={containerRef}
        className="readonly-enforcer-container"
        data-testid="readonly-enforcer"
        style={{ position: 'relative' }}
      >
        {/* Screen reader announcement for read-only state */}
        <div
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          role="status"
          style={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }}
        >
          This dashboard is in read-only mode. All interactive elements are disabled.
        </div>

        {/* Visual indicator overlay for read-only state */}
        <div
          className="readonly-indicator"
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            zIndex: 1000,
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            border: '1px solid rgba(25, 118, 210, 0.3)',
            borderRadius: '12px',
            padding: '2px 8px',
            fontSize: '0.7rem',
            color: '#1976d2',
            fontWeight: 500,
            pointerEvents: 'none',
            opacity: 0.8
          }}
          aria-hidden="true"
        >
          Read-Only
        </div>

        {/* Render the wrapped component */}
        <WrappedComponent {...props} ref={ref} />
      </div>
    );
  });

  // Set display name for debugging
  ReadOnlyEnforcedComponent.displayName = `withReadOnlyEnforcer(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return ReadOnlyEnforcedComponent;
}

/**
 * ReadOnlyEnforcer Component (alternative usage as a wrapper component)
 * 
 * Can be used as a wrapper component instead of HOC
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to enforce read-only on
 * @param {boolean} [props.enabled] - Whether to enable read-only enforcement (defaults to admin view state)
 * @returns {JSX.Element} ReadOnlyEnforcer component
 */
export function ReadOnlyEnforcer({ children, enabled }) {
  const { isAdminView } = useAdminContext();
  const shouldEnforce = enabled !== undefined ? enabled : isAdminView;

  // If enforcement is disabled, render children normally
  if (!shouldEnforce) {
    return <>{children}</>;
  }

  // Create a temporary component to wrap
  const TempComponent = ({ children }) => <>{children}</>;
  const EnforcedComponent = withReadOnlyEnforcer(TempComponent);

  return <EnforcedComponent>{children}</EnforcedComponent>;
}

export default withReadOnlyEnforcer;